/**
 * AriaLiveRegionHelper class
 * This class is designed to manage ARIA live regions for accessibility purposes.
 * It provides methods to create, update, and remove live regions dynamically.
 * The live regions are used to announce changes in content to assistive technologies.
 * 
 * Ensures only one global live region exists.
 * 
 * @class AriaLiveRegionHelper
 */
class AriaLiveRegionHelper {
  constructor(role = 'status') {
    // Singleton
    if (AriaLiveRegionHelper.instance) {
      return AriaLiveRegionHelper.instance;
    }
    
    this.role = role; 
    this.initLiveRegion();
    
    AriaLiveRegionHelper.instance = this;
  }

  /**
   * Initializes the ARIA live region.
   * Creates a live region element and appends it to the body.
   */
  initLiveRegion() {
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('role', this.role);
    this.liveRegion.classList.add('sr-only');
    document.body.appendChild(this.liveRegion);
  }

  /**
   * Removes the ARIA live region from the DOM.
   * Clears the singleton instance to allow new instances to be created.
   */
  removeLiveRegion() {
    if (this.liveRegion) {
      document.body.removeChild(this.liveRegion);
      this.liveRegion = null;
      AriaLiveRegionHelper.instance = null;
    } else {
      console.warn('Live region not found.');
    }
  }

  /**
   * Updates the content of the ARIA live region.
   * Clears content first to ensure screen readers announce repeated messages.
   * @param {string} message - The message to be announced.
   * @returns {void}
   */
  updateLiveRegion(message) {
    if (!this.liveRegion) {
      console.error('Live region not initialized.');
      return;
    }
    
    // Clear content first to ensure screen readers don't ignore repeated announcements
    this.liveRegion.textContent = '';
    
    // Use a small delay before setting the new message
    setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 100);
  }
}

// Initialize static property
AriaLiveRegionHelper.instance = null;

window.AriaLiveRegionHelper = AriaLiveRegionHelper;
